/* ============================================================
   Film developing order wizard.

   Everything it knows comes from window.FILM_WIZARD, which the Liquid
   snippet builds from the live products — ids, prices, option values and
   stock all come out of Shopify at render time. There is no hardcoded
   variant table to go stale when a price or an option name changes.

   Variants are matched by reading each product's own option names and
   values and normalising them to tokens, because the products don't agree
   with each other on wording: "Hold Negs" on one, "Hold onto my Negitives"
   on another. Matching on tokens means either spelling works.
   ============================================================ */

(function () {
  'use strict';

  var DATA = window.FILM_WIZARD;
  var root = document.querySelector('[data-film-wizard]');
  if (!DATA || !root) return;

  /* Every variant this wizard is able to add, split by what it is. Lets us
     read the cart back and tell our own lines apart from anything else the
     customer happens to be buying. */
  var FILM_IDS = {};
  var PRINT_IDS = {};
  var NEG_IDS = {};
  (function () {
    function mark(product, into) {
      if (!product) return;
      product.variants.forEach(function (v) {
        into[v.id] = true;
      });
    }
    DATA.formats.forEach(function (f) {
      mark(f.product, FILM_IDS);
    });
    mark(DATA.prints, PRINT_IDS);
    mark(DATA.negatives, NEG_IDS);
  })();

  function lineKind(variantId) {
    if (NEG_IDS[variantId]) return 'negatives';
    if (PRINT_IDS[variantId]) return 'prints';
    if (FILM_IDS[variantId]) return 'film';
    return null;
  }

  /* ---------- option normalising ---------- */

  // Which question does an option name answer?
  function optionRole(name) {
    var n = String(name || '').toLowerCase();
    if (n.indexOf('process') > -1) return 'process';
    if (n.indexOf('resolution') > -1 || n.indexOf('scan') > -1) return 'resolution';
    if (n.indexOf('shipping') > -1 || n.indexOf('negit') > -1 || n.indexOf('negative') > -1) return 'negatives';
    if (n.indexOf('film type') > -1) return 'filmtype';
    if (n.indexOf('quantity') > -1) return 'sets';
    if (n.indexOf('finish') > -1 || n.indexOf('paper') > -1) return 'finish';
    return null;
  }

  // Reduce a written option value to a token the wizard reasons about.
  function valueToken(role, value) {
    var v = String(value || '').toLowerCase();

    if (role === 'process') {
      if (v.indexOf('c-41') > -1 || v.indexOf('c41') > -1) return 'c41';
      if (v.indexOf('black') > -1 || v.indexOf('b&w') > -1) return 'bw';
      if (v.indexOf('e-6') > -1 || v.indexOf('e6') > -1) return 'e6';
      if (v.indexOf('ecn') > -1) return 'ecn2';
      if (v.indexOf('cross') > -1) return 'cross';
      return null;
    }

    if (role === 'resolution') {
      if (v.indexOf('standard') > -1) return 'standard';
      if (v.indexOf('jpg') > -1 || v.indexOf('jpeg') > -1) return 'jpg';
      if (v.indexOf('tiff') > -1) return 'tiff';
      if (v.indexOf('bmp') > -1) return 'bmp';
      return null;
    }

    if (role === 'negatives') {
      if (v.indexOf('hold') > -1) return 'hold';
      if (v.indexOf('bin') > -1 || v.indexOf("don't want") > -1 || v.indexOf('dont want') > -1) return 'bin';
      if (v.indexOf('with my prints') > -1 || v.indexOf('with prints') > -1) return 'with_prints';
      if (v.indexOf('with other') > -1 || v.indexOf('with my other') > -1) return 'with_other';
      if (v.indexOf('all my') > -1 || v.indexOf('all past') > -1) return 'back_all';
      if (v.indexOf('back') > -1 || v.indexOf('ship') > -1 || v.indexOf('send') > -1) return 'back';
      return null;
    }

    if (role === 'finish') {
      if (v.indexOf('gloss') > -1) return 'gloss';
      if (v.indexOf('matt') > -1) return 'matt';
      return null;
    }

    if (role === 'sets') {
      if (v.indexOf('two') > -1 || v.indexOf('2') > -1) return '2';
      if (v.indexOf('one') > -1 || v.indexOf('1') > -1) return '1';
      return null;
    }

    if (role === 'filmtype') return v.replace(/\s+/g, '');

    return null;
  }

  // Every token a product offers for a role, in the order Shopify has them.
  function tokensFor(product, role) {
    if (!product) return [];
    var out = [];
    product.options.forEach(function (name, i) {
      if (optionRole(name) !== role) return;
      product.variants.forEach(function (v) {
        var t = valueToken(role, v.options[i]);
        if (t && out.indexOf(t) === -1) out.push(t);
      });
    });
    return out;
  }

  function hasRole(product, role) {
    if (!product) return false;
    return product.options.some(function (name) {
      return optionRole(name) === role;
    });
  }

  // Find the cheapest available variant whose options satisfy `wanted`.
  // Roles the product doesn't have are ignored; roles it has but `wanted`
  // doesn't specify are left free.
  function findVariant(product, wanted, fallbacks) {
    if (!product) return null;
    var roles = product.options.map(optionRole);
    var best = null;

    product.variants.forEach(function (v) {
      for (var i = 0; i < roles.length; i++) {
        var role = roles[i];
        if (!role || !(role in wanted) || wanted[role] == null) continue;
        var token = valueToken(role, v.options[i]);
        var want = wanted[role];
        var alts = (fallbacks && fallbacks[role]) || [];
        if (token !== want && alts.indexOf(token) === -1) return;
      }
      if (!best || v.price < best.price) best = v;
    });

    return best;
  }

  function money(cents) {
    if (cents == null) return '';
    var s = (cents / 100).toFixed(2);
    return '$' + s.replace(/\.00$/, '');
  }

  function lowestPrice(product) {
    if (!product || !product.variants.length) return null;
    return product.variants.reduce(function (min, v) {
      return v.price < min ? v.price : min;
    }, product.variants[0].price);
  }

  /* ---------- labels ---------- */

  var PROCESS_LABEL = {
    c41: 'Colour (C-41)',
    bw: 'Black & white',
    e6: 'E-6 slide film',
    cross: 'Cross processed',
    ecn2: 'ECN-2 cine film',
  };
  var PROCESS_NOTE = {
    c41: 'Standard colour film. If the box says C-41, this is you.',
    bw: 'Traditional black and white development.',
    e6: 'Slide film, developed as positives.',
    cross: 'Slide film run through colour chemistry — shifted colour, hard contrast.',
    ecn2: 'Motion picture stock, remjet removed.',
  };
  var RES_LABEL = {
    standard: 'Standard scan',
    jpg: 'High resolution JPG',
    tiff: 'High resolution TIFF',
    bmp: 'High resolution BMP',
  };
  var RES_NOTE = {
    standard: '3360 × 2240 px · fine for prints up to 6×4 and anything on a screen',
    jpg: '5400 × 3600 px · room to crop and to print big',
    tiff: '5400 × 3600 px · uncompressed, for editing work',
    bmp: '5400 × 3600 px · uncompressed bitmap',
  };
  var NEG_LABEL = {
    hold: 'Hold them here',
    bin: 'Bin them',
    back: 'Send them back',
  };
  var NEG_NOTE = {
    hold: 'We file them in store. Ask for them any time.',
    bin: 'Disposed of after processing. Nothing to store, nothing to post.',
    back: 'Posted back to you — choose how below.',
  };

  // How the negatives choice reads back against each roll. Covers the return
  // sub-choices too, since that is one line either way.
  var NEG_SUMMARY = {
    hold: 'held here for you',
    bin: 'binned after scanning',
    back: 'posted back to you',
    back_all: 'posted back with the rest we hold',
    with_prints: 'posted back with your prints',
    with_other: 'posted back with the rest of your order',
  };

  // The same choice as the fee it carries. Priced once for the whole order,
  // not per roll, so it is worded as the job rather than as the outcome.
  var NEG_FEE_LABEL = {
    hold: 'Filing your negatives here',
    bin: 'Disposing of your negatives',
    back: 'Posting your negatives back',
    back_all: 'Posting back every negative we hold',
    with_prints: 'Posting your negatives with your prints',
    with_other: 'Sending negatives with the rest of your order',
  };

  // Longer explanation shown under an option once it's picked. Short notes go
  // on the tile; this is the "am I sure?" text.
  var DETAIL = {
    process: {
      c41: 'The standard colour process — nearly every colour film sold today is C-41. If the box says C-41, Gold, ColorPlus, Portra, Superia or similar, this is the one. It runs most often, so it comes back quickest.',
      bw: 'True black and white development in its own chemistry. HP5, Tri-X, Fomapan and the rest. We run it in smaller batches than colour, so allow a little longer.',
      e6: 'Slide film developed as positives — actual transparencies you can hold up to the light. Velvia, Provia, Ektachrome. Runs less often than colour, so expect a longer wait.',
      cross: 'Slide film put through colour chemistry on purpose. Colours shift, contrast goes hard, grain lifts. Unpredictable by design, and it cannot be undone — pick it because you want that look.',
      ecn2: 'Motion picture stock — Vision3, Cinestill and friends. The black backing has to come off before it can be developed, which is why it is its own process. Gives you the colour you see in films.',
    },
    resolution: {
      standard: '3360 × 2240 pixels, about 7.5 megapixels. Plenty for phones, screens, social media and prints up to 6×4. This is what most people take.',
      jpg: '5400 × 3600 pixels, about 19 megapixels, saved as JPGs. Worth the extra if you want to crop in hard or print bigger than 6×4.',
      tiff: 'Same 5400 × 3600 pixels, saved uncompressed as TIFFs. Much bigger files with nothing thrown away — the one to pick if you plan to edit properly.',
      bmp: 'Same resolution again, saved as uncompressed bitmaps. Only worth choosing if something you use specifically asks for BMP.',
    },
    prints: {
      no: 'Digital files only. You can always order prints later from the files we send you — nothing is lost by deciding now.',
      yes: 'A set of prints made from the roll at the same time as the scans, ready when your files are.',
    },
    finish: {
      gloss: 'Shiny surface, punchier colour and deeper blacks. What most people picture when they think of a photo print. Shows fingerprints.',
      matt: 'No shine at all. Softer, calmer, and much easier to look at under a lamp or behind glass in a frame.',
    },
    sets: {
      '1': 'One print of every frame on the roll.',
      '2': 'Two prints of every frame — one to keep and one to hand over, without paying to reprint later.',
    },
    negatives: {
      hold: 'We file them here under your name. They stay safe and flat, and you can ask for them any time you are in — most people choose this and forget about it.',
      bin: 'Disposed of once the scans are done. Nothing to store and nothing to post, but there is no going back to the film later — your scans become the only copy.',
      back: 'Posted back to you once we are finished with them. You choose how on the next question.',
    },
    negSub: {
      back: 'Sent on their own as soon as they come off the machine — the fastest way to get them back.',
      back_all: 'We pull out every negative we are holding for you and send the lot together, not just this roll.',
      with_prints: 'Held until your prints are ready so everything travels in one parcel.',
      with_other: 'Held until the rest of your order is ready to ship, so you pay one lot of postage.',
    },
  };

  /* ---------- state ---------- */

  var state = {
    format: null,
    process: null,
    resolution: null,
    prints: null, // 'yes' | 'no'
    finish: null,
    sets: null,
    negatives: null, // 'hold' | 'bin' | 'back'
    negSub: null, // 'back' | 'back_all' | 'with_other' | 'with_prints'
    notes: '',
  };

  function currentFormat() {
    if (!state.format) return null;
    return DATA.formats.filter(function (f) {
      return f.key === state.format;
    })[0];
  }

  function currentProduct() {
    var f = currentFormat();
    return f ? f.product : null;
  }

  function needsResolution() {
    return hasRole(currentProduct(), 'resolution');
  }

  /* ---------- what goes in the cart ---------- */

  function filmVariant() {
    var product = currentProduct();
    if (!product || !state.process) return null;

    var wanted = { process: state.process };
    if (needsResolution() && state.resolution) wanted.resolution = state.resolution;

    if (state.negatives === 'hold') wanted.negatives = 'hold';
    else if (state.negatives === 'bin') wanted.negatives = 'bin';
    else if (state.negatives === 'back') {
      wanted.negatives = state.negSub === 'with_other' ? 'with_other' : 'back';
    }

    // Products word the negatives option differently; accept the near misses.
    var fallbacks = {
      negatives: state.negatives === 'back' ? ['back', 'back_all', 'with_prints', 'with_other'] : [],
    };

    return findVariant(product, wanted, fallbacks);
  }

  function printsVariant() {
    if (state.prints !== 'yes' || !DATA.prints) return null;
    return findVariant(DATA.prints, { finish: state.finish, sets: state.sets }, null);
  }

  function negativesVariant() {
    if (!DATA.negatives || !state.negatives) return null;
    if (state.negatives === 'bin') return null;

    var token = state.negatives === 'hold' ? 'hold' : state.negSub || 'back';
    var product = DATA.negatives;
    var idx = product.options.map(optionRole).indexOf('negatives');
    if (idx === -1) idx = 0;

    var match = null;
    product.variants.forEach(function (v) {
      if (valueToken('negatives', v.options[idx]) === token && !match) match = v;
    });
    return match;
  }

  function chargeableNegatives() {
    return negFeeInCart ? null : negativesVariant();
  }

  function total() {
    var sum = 0;
    [filmVariant(), printsVariant(), chargeableNegatives()].forEach(function (v) {
      if (v) sum += v.price;
    });
    return sum;
  }


  /* ---------- steps ----------
     One question on screen at a time. The list of questions is rebuilt from
     the current answers (110 has no scan step, prints only ask finish and
     sets if you want prints), so navigation works on step keys, not indexes. */

  var stepsEl = root.querySelector('[data-steps]');

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function buildSteps() {
    var steps = [];
    var product = currentProduct();

    steps.push({
      key: 'format',
      short: 'Film',
      title: 'What are you developing?',
      hint: 'Not sure which one you have? The pictures are life-size shapes — match yours to one of them.',
      big: true,
      options: DATA.formats.map(function (f) {
        var from = lowestPrice(f.product);
        return {
          value: f.key,
          label: f.label,
          note: f.note,
          price: from != null ? 'from ' + money(from) : null,
          image: f.image,
          detail: f.detail,
        };
      }),
    });

    if (!state.format) return steps;

    steps.push({
      key: 'process',
      short: 'Process',
      title: 'How should it be processed?',
      hint: 'Only what we can run for this format is shown. If the box says C-41, take the first one.',
      options: tokensFor(product, 'process')
        .filter(function (t) {
          return DATA.disabled.indexOf(t) === -1;
        })
        .map(function (t) {
          return { value: t, label: PROCESS_LABEL[t] || t, note: PROCESS_NOTE[t] };
        }),
    });

    if (!state.process) return steps;

    if (needsResolution()) {
      var base = findVariant(product, { process: state.process, resolution: 'standard' }, null);
      steps.push({
        key: 'resolution',
        short: 'Scans',
        title: 'How should we scan it?',
        hint: 'Scans are emailed as a download link.',
        options: tokensFor(product, 'resolution')
          .filter(function (t) {
            return DATA.disabled.indexOf(t) === -1;
          })
          .map(function (t) {
            var v = findVariant(product, { process: state.process, resolution: t }, null);
            return {
              value: t,
              label: RES_LABEL[t] || t,
              note: RES_NOTE[t],
              price: v && base && v.price > base.price ? '+' + money(v.price - base.price) : 'included',
            };
          }),
      });
      if (!state.resolution) return steps;
    }

    var printFrom = lowestPrice(DATA.prints);
    steps.push({
      key: 'prints',
      short: 'Prints',
      title: 'Do you want prints as well?',
      options: [
        { value: 'no', label: 'Scans only', note: 'Digital files, nothing printed' },
        {
          value: 'yes',
          label: 'Prints too',
          note: 'A set of prints from the roll',
          price: printFrom != null ? 'from ' + money(printFrom) : null,
        },
      ],
    });

    if (!state.prints) return steps;

    if (state.prints === 'yes') {
      steps.push({
        key: 'finish',
        short: 'Finish',
        title: 'Print finish',
        options: tokensFor(DATA.prints, 'finish').map(function (t) {
          return {
            value: t,
            label: t === 'gloss' ? 'Gloss' : 'Matt',
            note: t === 'gloss' ? 'Shiny, punchy colour' : 'No reflections, softer look',
          };
        }),
      });
      if (!state.finish) return steps;

      steps.push({
        key: 'sets',
        short: 'Sets',
        title: 'How many sets?',
        options: tokensFor(DATA.prints, 'sets').map(function (t) {
          var v = findVariant(DATA.prints, { finish: state.finish, sets: t }, null);
          return {
            value: t,
            label: t === '2' ? 'Two sets' : 'One set',
            note: t === '2' ? 'One to keep, one to give away' : null,
            price: v ? money(v.price) : null,
          };
        }),
      });
      if (!state.sets) return steps;
    }

    steps.push({
      key: 'negatives',
      short: 'Negatives',
      title: 'What should we do with your negatives?',
      options: ['hold', 'bin', 'back'].map(function (t) {
        var fee = null;
        if (t !== 'bin') {
          var a = state.negatives,
            b = state.negSub;
          state.negatives = t;
          state.negSub = t === 'back' ? 'back' : null;
          var v = negativesVariant();
          state.negatives = a;
          state.negSub = b;
          if (v) fee = v.price ? money(v.price) : 'free';
        }
        return { value: t, label: NEG_LABEL[t], note: NEG_NOTE[t], price: fee };
      }),
    });

    if (!state.negatives) return steps;

    if (state.negatives === 'back') {
      var subs = [
        ['back', 'On their own', 'Posted as soon as they come off the machine'],
        ['back_all', 'With the ones you hold', 'We gather up the negatives on file and send the lot'],
        ['with_other', 'With the rest of my order', 'Held until everything else ships'],
      ];
      if (state.prints === 'yes') {
        subs.splice(1, 0, ['with_prints', 'With my prints', 'They travel together in one parcel']);
      }
      steps.push({
        key: 'negSub',
        short: 'Return',
        title: 'How should they come back?',
        options: subs.map(function (s) {
          var saved = state.negSub;
          state.negSub = s[0];
          var v = negativesVariant();
          state.negSub = saved;
          return { value: s[0], label: s[1], note: s[2], price: v ? (v.price ? money(v.price) : 'free') : null };
        }),
      });
      if (!state.negSub) return steps;
    }
    return steps;
  }

  /* ---------- rendering ----------
     Every question that applies is on the page at once, stacked. Answering
     one reveals the next below it; the ones above stay exactly where they
     were, so nothing you have already read moves. */

  var flash = '';

  // What the cart holds right now. Rolls added earlier in this sitting live
  // here and nowhere else — the wizard keeps no separate tally, so a roll
  // removed on the cart page disappears from the summary too.
  var cart = { lines: [], otherCount: 0, otherPrice: 0, total: 0 };

  // The negatives fee is charged once per order. If the cart already carries
  // one — from an earlier roll or an earlier visit — it must not be counted
  // again, or the total we quote is higher than the cart charges.
  var negFeeInCart = false;

  function refreshCart(thenRender) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var lines = [];
        var otherCount = 0;
        var otherPrice = 0;

        data.items.forEach(function (item) {
          if (lineKind(item.variant_id)) {
            lines.push(item);
          } else {
            otherCount += item.quantity;
            otherPrice += item.final_line_price;
          }
        });

        cart = { lines: lines, otherCount: otherCount, otherPrice: otherPrice, total: data.total_price };
        negFeeInCart = lines.some(function (item) {
          return DATA.negativeVariantIds.indexOf(item.variant_id) > -1;
        });
        if (thenRender) render();
      })
      .catch(function () {});
  }

  function render() {
    var steps = buildSteps();

    stepsEl.innerHTML =
      (flash ? '<p class="fw-flash" role="status">' + esc(flash) + '</p>' : '') +
      bankedBarHtml() +
      steps
        .map(function (step, n) {
          var chosen = null;
          step.options.forEach(function (o) {
            if (o.value === state[step.key]) chosen = o;
          });

          return (
            '<section class="fw-step' + (chosen ? ' is-answered' : '') + '">' +
            '<h3 class="fw-step__title">' +
            '<span class="fw-step__n">' + (n + 1) + '</span>' +
            esc(step.title) +
            '</h3>' +
            (step.hint ? '<p class="fw-step__hint">' + esc(step.hint) + '</p>' : '') +
            // The count drives how wide the row is allowed to get, so a
            // two-way question doesn't stretch across the whole page.
            '<div class="fw-opts' + (step.big ? ' fw-opts--big' : '') + '"' +
            ' style="--fw-cols: ' + step.options.length + '">' +
            step.options
              .map(function (o) {
                var selected = state[step.key] === o.value;
                return (
                  '<button type="button" class="fw-opt' +
                  (step.big ? ' fw-opt--format' : '') +
                  (selected ? ' is-selected' : '') +
                  '" data-step="' + step.key + '" data-value="' + esc(o.value) + '"' +
                  ' aria-pressed="' + (selected ? 'true' : 'false') + '">' +
                  (o.image
                    ? '<img class="fw-opt__img" src="' + esc(o.image) + '" alt="" width="200" height="200" loading="lazy">'
                    : '') +
                  '<span class="fw-opt__label">' + esc(o.label) + '</span>' +
                  (o.price ? '<span class="fw-opt__price">' + esc(o.price) + '</span>' : '') +
                  (o.note ? '<span class="fw-opt__note">' + esc(o.note) + '</span>' : '') +
                  '</button>'
                );
              })
              .join('') +
            '</div>' +
            // Same question as a native menu — shown instead of the tiles on
            // phones, where a row of tiles means sideways scrolling.
            '<div class="fw-select">' +
            (step.big && chosen && chosen.image
              ? '<img class="fw-select__thumb" src="' + esc(chosen.image) + '" alt="" width="120" height="120">'
              : '') +
            '<select class="fw-select__control" data-select="' + step.key + '" ' +
            'aria-label="' + esc(step.title) + '">' +
            '<option value="">Choose…</option>' +
            step.options
              .map(function (o) {
                return (
                  '<option value="' + esc(o.value) + '"' +
                  (state[step.key] === o.value ? ' selected' : '') +
                  '>' +
                  esc(o.label) +
                  (o.price ? ' — ' + esc(o.price) : '') +
                  '</option>'
                );
              })
              .join('') +
            '</select>' +
            '</div>' +
            (chosen
              ? '<div class="fw-info">' +
                '<p class="fw-info__label">' +
                esc(chosen.label) +
                (chosen.price ? ' <span class="fw-info__price">' + esc(chosen.price) + '</span>' : '') +
                '</p>' +
                '<p class="fw-info__text">' +
                esc(chosen.detail || (DETAIL[step.key] && DETAIL[step.key][chosen.value]) || chosen.note || '') +
                '</p>' +
                '</div>'
              : '') +
            '</section>'
          );
        })
        .join('') +
      // Once every question is answered, the whole order lands at the foot of
      // the column where they finished reading. Until then Start again stands
      // on its own, since there is no finish row for it to join yet.
      (isComplete()
        ? doneHtml()
        : state.format
          ? '<div class="fw-done__actions fw-restart">' + restartBtnHtml() + '</div>'
          : '');
  }

  function restartBtnHtml() {
    return '<button type="button" class="fw-done__btn fw-done__btn--quiet" data-reset>Start again</button>';
  }

  /* ---------- the order, as one list ----------
     Rolls banked earlier are read back out of the cart rather than
     remembered, so the summary is the cart and cannot drift from it. */

  function rollHead(item) {
    var p = item.properties || {};
    var bits = [];
    if (p.Format) bits.push(p.Format);
    if (p.Processing) bits.push(p.Processing);
    return bits.length ? bits.join(' · ') : item.product_title;
  }

  function developLine(scans) {
    return 'Developing' + (scans ? ' · ' + String(scans).toLowerCase() : ' and scanning');
  }

  function printsLine(sets, finish) {
    return (sets === '2' || sets === 'Two' ? 'Two sets' : 'One set') + ' of prints · ' + String(finish || '').toLowerCase();
  }

  // What happens to this roll's negatives. It costs nothing here — every film
  // variant is the same price whichever way it goes — but it belongs against
  // the roll, because it is a decision made per roll and the fee below is not.
  function negativesLine(token) {
    if (!token || !NEG_SUMMARY[token]) return null;
    return ['Negatives ' + NEG_SUMMARY[token], 'included'];
  }

  // Cart lines this wizard added, grouped back into the rolls they came from.
  // Each add carries a hidden _roll property; anything without one (an older
  // cart) stands on its own rather than being guessed at.
  function bankedRolls() {
    var byKey = {};
    var order = [];

    cart.lines.forEach(function (item) {
      var kind = lineKind(item.variant_id);
      if (!kind || kind === 'negatives') return; // negatives are order-level

      var key = (item.properties && item.properties._roll) || 'line-' + item.key;
      if (!byKey[key]) {
        byKey[key] = { head: '', qty: 1, rows: [] };
        order.push(key);
      }
      var roll = byKey[key];
      var price = money(item.final_line_price);

      if (kind === 'film') {
        var props = item.properties || {};
        roll.head = rollHead(item);
        roll.qty = item.quantity;
        roll.rows.unshift([developLine(props.Scans), price]);
        roll.negatives = negativesLine(valueToken('negatives', props.Negatives));
      } else {
        var p = item.properties || {};
        roll.rows.push([p.Sets || p.Finish ? printsLine(p.Sets, p.Finish) : item.variant_title || item.product_title, price]);
      }
    });

    // The negatives line goes last, under the prints it may travel with.
    return order.map(function (k) {
      var roll = byKey[k];
      if (roll.negatives) roll.rows.push(roll.negatives);
      return roll;
    });
  }

  // The roll being answered right now, in the same shape.
  function pendingRoll() {
    var f = currentFormat();
    if (!f) return null;

    var film = filmVariant();
    var rows = [[developLine(state.resolution ? RES_LABEL[state.resolution] : null), film ? money(film.price) : '—']];

    var prints = printsVariant();
    if (prints) rows.push([printsLine(state.sets, state.finish === 'gloss' ? 'Gloss' : 'Matt'), money(prints.price)]);

    var negRow = negativesLine(state.negatives === 'back' ? state.negSub || 'back' : state.negatives);
    if (negRow) rows.push(negRow);

    return {
      head: f.label + (state.process ? ' · ' + (PROCESS_LABEL[state.process] || '') : ''),
      qty: 1,
      rows: rows,
      pending: true,
    };
  }

  // Charged once for the whole order, not per roll: the negatives fee, and
  // anything the customer was already buying before they got here.
  function extraRows(rollCount) {
    var rows = [];

    // Whichever came first wins — the cart line if one is already banked,
    // otherwise what this roll has asked for.
    var negLine = cart.lines.filter(function (item) {
      return lineKind(item.variant_id) === 'negatives';
    })[0];

    var token = null;
    var price = null;

    if (negLine) {
      token = valueToken('negatives', negLine.variant_title || negLine.product_title);
      price = negLine.final_line_price;
    } else if (state.negatives) {
      var v = negativesVariant();
      token = state.negatives === 'back' ? state.negSub || 'back' : state.negatives;
      price = v ? v.price : null;
    }

    if (token && price) {
      rows.push([
        (NEG_FEE_LABEL[token] || 'Handling your negatives') + (rollCount > 1 ? ' · once for the order' : ''),
        money(price),
      ]);
    }

    if (cart.otherCount) {
      rows.push([
        cart.otherCount + (cart.otherCount === 1 ? ' other item' : ' other items') + ' already in your cart',
        money(cart.otherPrice),
      ]);
    }

    return rows;
  }

  // What the cart will actually charge: everything already in it, plus the
  // roll about to be added.
  function grandTotal() {
    return cart.total + total();
  }

  function rowsHtml(rows, extraClass) {
    if (!rows.length) return '';
    return (
      '<dl class="fw-done__rows' + (extraClass ? ' ' + extraClass : '') + '">' +
      rows
        .map(function (r) {
          return '<dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd>';
        })
        .join('') +
      '</dl>'
    );
  }

  function doneHtml() {
    var rolls = bankedRolls();
    var pending = pendingRoll();
    if (pending) rolls = rolls.concat([pending]);
    var many = rolls.length > 1;

    return (
      '<section class="fw-done">' +
      '<p class="fw-done__eyebrow">That\'s everything</p>' +
      '<h3 class="fw-done__title">Your order</h3>' +
      '<ol class="fw-done__rolls">' +
      rolls
        .map(function (r, i) {
          return (
            '<li class="fw-done__roll' + (r.pending && many ? ' is-pending' : '') + '">' +
            '<p class="fw-done__roll-head">' +
            '<span class="fw-done__roll-n">Roll ' + (i + 1) + '</span>' +
            '<span class="fw-done__roll-name">' + esc(r.head || 'Film') + '</span>' +
            (r.qty > 1 ? '<span class="fw-done__roll-qty">× ' + r.qty + '</span>' : '') +
            (r.pending && many ? '<span class="fw-done__roll-flag">adding now</span>' : '') +
            '</p>' +
            rowsHtml(r.rows) +
            '</li>'
          );
        })
        .join('') +
      '</ol>' +
      rowsHtml(extraRows(rolls.length), 'fw-done__rows--extras') +
      '<p class="fw-done__total"><span>Order total</span><span>' + money(grandTotal()) + '</span></p>' +
      '<label class="fw-done__notes-label" for="fw-notes">Anything we should know?</label>' +
      '<textarea id="fw-notes" class="fw-done__notes" rows="2" ' +
      'placeholder="Push processing, a roll that matters more than the others, a deadline…">' +
      esc(state.notes) +
      '</textarea>' +
      '<div class="fw-done__actions">' +
      '<button type="button" class="fw-done__btn" data-submit="checkout">Finish order</button>' +
      '<button type="button" class="fw-done__btn fw-done__btn--ghost" data-submit="another">' +
      'Add another film to order' +
      '</button>' +
      restartBtnHtml() +
      '</div>' +
      '<p class="fw-done__note">' + esc(DATA.fineprint || '') + '</p>' +
      '<p class="fw-done__error" data-error hidden></p>' +
      '</section>'
    );
  }

  // While they are part-way through another roll, a quiet line saying what is
  // already banked — and a way out if this roll was one too many.
  function bankedBarHtml() {
    if (isComplete()) return '';
    var rolls = bankedRolls();
    if (!rolls.length) return '';

    return (
      '<div class="fw-banked">' +
      '<p class="fw-banked__count">' +
      rolls.length +
      (rolls.length === 1 ? ' roll' : ' rolls') +
      ' already in your order' +
      '</p>' +
      '<p class="fw-banked__sum">' + money(cart.total) + ' so far</p>' +
      '<a class="fw-banked__link" href="/cart">Finish order without adding another →</a>' +
      '</div>'
    );
  }

  function isComplete() {
    if (!state.format || !state.process) return false;
    if (needsResolution() && !state.resolution) return false;
    if (!state.prints) return false;
    if (state.prints === 'yes' && (!state.finish || !state.sets)) return false;
    if (!state.negatives) return false;
    if (state.negatives === 'back' && !state.negSub) return false;
    return !!filmVariant();
  }

  /* ---------- interaction ---------- */

  function choose(step, value) {
    flash = '';
    state[step] = value || null;

    // A change upstream invalidates everything that depended on it.
    if (step === 'format') {
      state.process = state.resolution = state.prints = null;
      state.finish = state.sets = state.negatives = state.negSub = null;
    }
    if (step === 'process') state.resolution = null;
    if (step === 'prints' && value === 'no') state.finish = state.sets = null;
    if (step === 'finish') state.sets = null;
    if (step === 'negatives' && value !== 'back') state.negSub = null;

    render();
  }

  root.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-select]');
    if (sel) choose(sel.getAttribute('data-select'), sel.value);
  });

  root.addEventListener('click', function (e) {
    var opt = e.target.closest('[data-step]');
    if (opt && !opt.disabled) {
      choose(opt.getAttribute('data-step'), opt.getAttribute('data-value'));
      return;
    }

    var submit = e.target.closest('[data-submit]');
    if (submit) addToCart(submit.getAttribute('data-submit') || 'checkout');

    if (e.target.closest('[data-reset]')) {
      // Easy to hit by accident several questions in, and it throws away
      // answers — so it asks first, and says what it will not touch.
      var banked = bankedRolls().length;
      var warning = banked
        ? 'Start again? This clears the answers for the roll you are on. ' +
          (banked === 1 ? 'The roll' : 'The ' + banked + ' rolls') +
          ' already in your order will stay there.'
        : 'Start again? This clears the answers you have given so far.';
      if (!window.confirm(warning)) return;

      Object.keys(state).forEach(function (k) {
        state[k] = k === 'notes' ? '' : null;
      });
      render();
      // Put them back at the first question rather than leaving them stranded
      // where the last one used to be.
      var top = document.getElementById('fd-order') || root;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      top.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  });

  root.addEventListener('input', function (e) {
    if (e.target.id === 'fw-notes') state.notes = e.target.value;
  });

  function addToCart(mode) {
    var buttons = [].slice.call(root.querySelectorAll('[data-submit]'));
    var film = filmVariant();
    if (!film) return;

    // A tag shared by every line from this roll. Underscore-prefixed, so
    // Shopify hides it from the cart and the order; it is only here so the
    // summary can put a roll's prints back with the roll they belong to, and
    // so two identical rolls stay two lines instead of merging into one.
    var rollTag = 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

    var items = [
      {
        id: film.id,
        quantity: 1,
        properties: {
          Format: currentFormat().label,
          Processing: PROCESS_LABEL[state.process] || '',
          Negatives: NEG_LABEL[state.negatives] || '',
          Notes: state.notes || '',
          _roll: rollTag,
        },
      },
    ];
    if (state.resolution) items[0].properties.Scans = RES_LABEL[state.resolution];

    var prints = printsVariant();
    if (prints) {
      items.push({
        id: prints.id,
        quantity: 1,
        properties: {
          Finish: state.finish === 'gloss' ? 'Gloss' : 'Matt',
          Sets: state.sets === '2' ? 'Two' : 'One',
          _roll: rollTag,
        },
      });
    }

    buttons.forEach(function (b) {
      b.disabled = true;
      b.textContent = 'Adding…';
    });
    [].slice.call(root.querySelectorAll('[data-error]')).forEach(function (el) {
      el.hidden = true;
    });

    // The negatives fee is one line per order, not one per roll.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (fresh) {
        var negs = negativesVariant();
        var already =
          negs &&
          fresh.items.some(function (item) {
            return DATA.negativeVariantIds.indexOf(item.variant_id) > -1;
          });
        negFeeInCart = !!already;
        if (negs && !already) items.push({ id: negs.id, quantity: 1 });

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });
      })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) throw new Error(body.description || body.message || 'That did not go through.');
          return body;
        });
      })
      .then(function () {
        if (mode === 'another') {
          // Bank this roll and hand back a blank form. The count comes back
          // off the cart on the next refresh, so nothing is tallied twice.
          Object.keys(state).forEach(function (k) {
            state[k] = k === 'notes' ? '' : null;
          });
          flash = 'Added. Answer the questions again for the next roll — it all goes into the same order.';
          refreshCart(true);
          render();
          var top = document.getElementById('fd-order') || root;
          var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          top.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
          return;
        }
        window.location.href = '/cart';
      })
      .catch(function (err) {
        render();
        [].slice.call(root.querySelectorAll('[data-error]')).forEach(function (el) {
          el.textContent = err.message + ' Nothing has been charged — try again, or come in and we will sort it.';
          el.hidden = false;
        });
      });
  }

  render();
  refreshCart(true);
})();
